<template>
  <div class="tr-page">
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Trainers</h1>
        <p class="pg-title-sub">Review, edit and manage trainer account details</p>
      </div>
    </div>

    <section class="tr-table-card">
      <div class="tr-table-header">
        <span class="tr-header-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 10L12 5 2 10l10 5 10-5z"/>
            <path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/>
          </svg>
        </span>
        <div>
          <h2 class="tr-section-title">All Trainers</h2>
          <p class="tr-section-subtitle">Trainer identity and account access list</p>
        </div>
      </div>

      <div v-if="loading" class="tr-table-wrap">
        <table class="tr-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Trainer name</th>
              <th>Nickname</th>
              <th>E-mail</th>
              <th class="tr-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="n in 6" :key="'trainer-skeleton-row-' + n">
              <td><div class="tr-sk-id sk-shimmer"></div></td>
              <td><div class="tr-sk-name sk-shimmer"></div></td>
              <td><div class="tr-sk-short sk-shimmer"></div></td>
              <td><div class="tr-sk-email sk-shimmer"></div></td>
              <td>
                <div class="tr-row-actions">
                  <div class="tr-sk-action sk-shimmer"></div>
                  <div class="tr-sk-action sk-shimmer"></div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="trainers.length === 0" class="tr-empty">
        <span class="tr-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="4"/>
            <path d="M4 21a8 8 0 0 1 16 0"/>
            <path d="M9 15h6"/>
          </svg>
        </span>
        <h2 class="tr-empty-title">No trainers found</h2>
        <p class="tr-empty-text">Trainer records will appear here once they are available.</p>
      </div>

      <div v-else class="tr-table-wrap">
        <table class="tr-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Trainer name</th>
              <th>Nickname</th>
              <th>E-mail</th>
              <th class="tr-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(trainer, index) in trainers" :key="trainer.id || index">
              <td>
                <span class="tr-id-pill">{{ trainerId(trainer) }}</span>
              </td>
              <td>
                <div class="tr-name-cell">
                  <span class="tr-avatar" aria-hidden="true">{{ trainerInitials(trainer) }}</span>
                  <span class="tr-name-text">{{ trainerName(trainer) }}</span>
                </div>
              </td>
              <td>{{ trainerNickname(trainer) }}</td>
              <td class="tr-email-cell">{{ trainerEmail(trainer) }}</td>
              <td>
                <div class="tr-row-actions">
                  <router-link
                    class="tr-action-btn"
                    :to="'/admin/trainers/edit/' + trainer.id"
                    :aria-label="'Edit ' + trainerName(trainer)"
                    title="Edit trainer">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 20h9"/>
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                    </svg>
                  </router-link>
                  <button
                    class="tr-action-btn tr-action-btn--danger"
                    type="button"
                    :disabled="disabled"
                    :aria-label="'Remove ' + trainerName(trainer)"
                    title="Remove trainer"
                    @click="removeTrainer(trainer.id)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M3 6h18"/>
                      <path d="M8 6V4h8v2"/>
                      <path d="M19 6l-1 14H6L5 6"/>
                      <path d="M10 11v5"/>
                      <path d="M14 11v5"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="!loading && totalPages > 0" class="tr-pagination-footer">
      <div class="tr-pagination-summary">
        Page {{ currentPage + 1 }} of {{ totalPages }}
      </div>
      <paginate
        :page-count="totalPages"
        :click-handler="handlePaginate"
        :prev-text="'Prev'"
        :next-text="'Next'"
        :container-class="'tr-pagination'"
        :page-class="'tr-pagination-item'"
        :page-link-class="'tr-pagination-link'"
        :prev-class="'tr-pagination-item'"
        :prev-link-class="'tr-pagination-link'"
        :next-class="'tr-pagination-item'"
        :next-link-class="'tr-pagination-link'"
        :break-view-class="'tr-pagination-item'"
        :break-view-link-class="'tr-pagination-link'"
        :active-class="'tr-pagination-active'"
        :disabled-class="'tr-pagination-disabled'">
      </paginate>
    </div>
  </div>
</template>

<script>
import Paginate from 'vuejs-paginate'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'allTrainers',
  data: () => ({
    currentPage: 0,
    disabled: false,
    loading: false
  }),
  computed: {
    ...mapGetters({
      trainers: 'trainers/allTrainers',
      totalPages: 'trainers/totalPages'
    })
  },
  methods: {
    async loadTrainers (page) {
      const nextPage = typeof page === 'number' ? page : this.currentPage

      try {
        this.loading = true
        this.currentPage = nextPage
        await this.getAll({ page: nextPage })
      } finally {
        this.loading = false
      }
    },
    async removeTrainer (id) {
      const optionsModal = {
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, remove the trainer!'
      }

      const confirmModal = await this.$swal(optionsModal)

      if (confirmModal.value) {
        try {
          this.disabled = true
          this.loading = true

          const isRemoved = await this.remove(id)

          await this.loadTrainers(this.currentPage)

          if (isRemoved) {
            this.$swal(
              'Deleted!',
              'The trainer has been removed.',
              'success'
            )
          }
        } finally {
          this.disabled = false
          this.loading = false
        }
      }
    },
    handlePaginate (page) {
      this.loadTrainers(page - 1)
    },
    displayValue (value, fallback) {
      const emptyFallback = fallback || '-'
      if (value === 0) {
        return '0'
      }
      if (value === false) {
        return 'No'
      }
      if (value === true) {
        return 'Yes'
      }
      if (value === null || value === undefined) {
        return emptyFallback
      }
      const stringValue = String(value).trim()
      return stringValue ? stringValue : emptyFallback
    },
    trainerId (trainer) {
      return this.displayValue(trainer && trainer.id)
    },
    trainerName (trainer) {
      if (!trainer) {
        return 'Unnamed trainer'
      }
      const name = [
        this.displayValue(trainer.firstName, ''),
        this.displayValue(trainer.lastName, '')
      ].join(' ').trim()

      return name || 'Unnamed trainer'
    },
    trainerInitials (trainer) {
      const name = this.trainerName(trainer)
      if (name === 'Unnamed trainer') {
        return 'TR'
      }
      return name.split(' ').map(function (part) {
        return part.charAt(0)
      }).join('').slice(0, 2).toUpperCase()
    },
    trainerNickname (trainer) {
      return this.displayValue(trainer && trainer.nickname)
    },
    trainerEmail (trainer) {
      return this.displayValue(trainer && trainer.user && trainer.user.email)
    },
    ...mapActions({
      getAll: 'trainers/getAllTrainers',
      remove: 'trainers/removeTrainer',
      clearAllTrainers: 'trainers/clearAllTrainers'
    })
  },
  mounted () {
    this.loadTrainers(0)
  },
  beforeDestroy () {
    this.clearAllTrainers()
  },
  components: {
    Paginate
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.tr-page {
  font-family: 'Inter', sans-serif;
  color: #1a1a2e;
}

.tr-table-card,
.tr-empty,
.tr-pagination-footer {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.tr-table-card {
  overflow: hidden;
}

.tr-table-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid #f0f0f5;
}

.tr-header-icon,
.tr-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
}

.tr-header-icon svg,
.tr-empty-icon svg,
.tr-action-btn svg {
  width: 16px;
  height: 16px;
}

.tr-section-title {
  color: #1a1a2e;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.tr-section-subtitle {
  color: #8a8a9a;
  font-size: 12px;
  margin: 3px 0 0;
}

.tr-table-wrap {
  overflow-x: auto;
  width: 100%;
}

.tr-table {
  width: 100%;
  min-width: 780px;
  border-collapse: collapse;
}

.tr-table th,
.tr-table td {
  border-bottom: 1px solid #f0f0f5;
  padding: 13px 16px;
  text-align: left;
  vertical-align: middle;
}

.tr-table th {
  background: #fafafa;
  color: #8a8a9a;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.tr-table td {
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
}

.tr-table tbody tr:hover td {
  background: #fbfbff;
}

.tr-table tbody tr:last-child td {
  border-bottom: none;
}

.tr-actions-col {
  width: 96px;
}

.tr-id-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border-radius: 20px;
  background: #f0effe;
  color: #413b94;
  font-size: 11px;
  font-weight: 800;
  padding: 2px 9px;
}

.tr-name-cell,
.tr-row-actions {
  display: flex;
  align-items: center;
}

.tr-name-cell {
  gap: 10px;
  min-width: 0;
}

.tr-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f0effe;
  color: #5b4fcf;
  font-size: 11px;
  font-weight: 800;
}

.tr-name-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tr-email-cell {
  overflow-wrap: anywhere;
}

.tr-row-actions {
  gap: 8px;
}

.tr-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #e8e8ed;
  border-radius: 50%;
  background: #fff;
  color: #5b4fcf;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
}

.tr-action-btn:hover {
  background: #5b4fcf;
  border-color: #5b4fcf;
  color: #fff;
}

.tr-action-btn--danger {
  color: #c0392b;
}

.tr-action-btn--danger:hover {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

.tr-action-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.tr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  margin: 1.25rem;
  padding: 2rem;
  text-align: center;
}

.tr-empty-title {
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
  margin: 12px 0 4px;
}

.tr-empty-text {
  color: #8a8a9a;
  font-size: 13px;
  margin: 0;
}

.tr-pagination-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.85rem 1rem;
}

.tr-pagination-summary {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 600;
}

.tr-pagination {
  display: flex;
  align-items: center;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.tr-pagination-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  border: 1px solid #e8e8ed;
  border-radius: 7px;
  color: #5b4fcf;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 0 9px;
  text-decoration: none;
}

.tr-pagination-link:hover {
  background: #f0effe;
}

.tr-pagination-active .tr-pagination-link {
  background: #5b4fcf;
  border-color: #5b4fcf;
  color: #fff;
}

.tr-pagination-disabled .tr-pagination-link {
  color: #b8b8c8;
  cursor: not-allowed;
}

.tr-sk-id {
  width: 52px;
  height: 24px;
}

.tr-sk-name {
  width: 190px;
  height: 18px;
}

.tr-sk-short {
  width: 120px;
  height: 18px;
}

.tr-sk-email {
  width: 240px;
  height: 18px;
}

.tr-sk-action {
  width: 30px;
  height: 30px;
  border-radius: 50%;
}

@media (max-width: 760px) {
  .tr-pagination-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .tr-table-header {
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// All.vue?97c5ae2e